import { getPlantDatabase } from '../modules/plantDatabaseLoader.js';
import { trackDownload } from '../modules/analytics.js';
import { loadExportLibraries } from '../modules/load-export-libs.js';

// Month names for table header
const MONTHS = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

// Color codes for activities
const ACTIVITY_COLORS = {
    sow: '#a3d977',      // greenish
    plant: '#f7d774',    // yellowish
    harvest: '#f79e9e'   // reddish
};

// Function to map plant database names to image filenames (WebP)
function getImageFileName(plantName) {
    const imageNameMap = {
        // Plural to singular mappings
        'Beets': 'Beet.webp',
        'Broad Beans': 'Broad bean.webp',
        'Carrots': 'Carrot.webp',
        'Leeks': 'Leek.webp',
        'Onions': 'Onion.webp',
        'Beans': 'Beans.webp',
        'Potatoes': 'Potato.webp',
        'Fennel': 'Fennel (2).webp',
        'Bok Choy': 'Bok Choy.webp',
        'Pak Choi': 'Bok Choy.webp',
        'Tomatoes': 'tomato.webp',
        'Peppers': 'Bell pepper.webp',
        'Bell Peppers': 'Bell pepper.webp',
        'Hot Peppers': 'Hot Pepper.webp',
        'Chili Peppers': 'Chilli Pepper.webp',
        'Green Beans': 'green beans.webp',
        'Snow Peas': 'Snap peas.webp',
        'Sugar Snap Peas': 'Sugar Snap peas.webp',
        'Swiss Chard': 'Swiss Chard.webp',
        'Brussel Sprout': 'Brussel Sprout.webp',
        'Brussels Sprouts': 'Brussel Sprout.webp'
    };
    return imageNameMap[plantName] || `${plantName}.webp`;
}

// Signature of last rendered plant set; skip full rebuild when unchanged
let lastCalendarSignature = '';

// Main function to update the calendar
export async function updateGardenCalendar() {
    const calendarContainer = document.querySelector('.calendar-tables');
    if (!calendarContainer) return;

    const plantDatabase = await getPlantDatabase();

    // One calendar block per base crop (same aggregation as Plant Information / summary table).
    // First-seen base order follows a single pass over the grid, like updateSummaryTable().
    const gridItems = document.querySelectorAll('.grid-item-5x5');
    const calendarBaseOrder = [];
    const seenBase = new Set();
    gridItems.forEach((item) => {
        const label = item.querySelector('.plant-label');
        if (!label) return;
        const displayName = label.textContent.trim();
        if (!displayName) return;

        let baseKey = (label.dataset.basePlant || '').trim() || null;
        if (!baseKey) {
            for (const dbName in plantDatabase) {
                if (dbName.toLowerCase() === displayName.toLowerCase()) {
                    baseKey = dbName;
                    break;
                }
            }
        }
        if (!baseKey) baseKey = displayName;

        if (seenBase.has(baseKey)) return;
        seenBase.add(baseKey);
        calendarBaseOrder.push(baseKey);
    });

    const signature = calendarBaseOrder.join('\n');
    if (signature === lastCalendarSignature) return;
    lastCalendarSignature = signature;

    calendarContainer.innerHTML = '';

    calendarBaseOrder.forEach((baseKey) => {
        const data = plantDatabase[baseKey];
        if (!data) return; // skip if base crop not in database

        // Create table
        const block = document.createElement('div');
        block.className = 'calendar-crop-block';
        const table = document.createElement('table');
        table.className = 'calendar-table';

        // Header
        const thead = document.createElement('thead');
        const headerRow1 = document.createElement('tr');
        headerRow1.style.backgroundColor = '#f0f0f0'; // Light blue-grey color
        const cropHeader = document.createElement('th');
        cropHeader.className = 'crop-header';
        cropHeader.textContent = 'Plant';
        cropHeader.rowSpan = 2;
        cropHeader.style.fontWeight = 'bold'; // Add font weight to crop header
        headerRow1.appendChild(cropHeader);
        const activityHeader = document.createElement('th');
        activityHeader.className = 'activity-header';
        activityHeader.textContent = 'Activity';
        activityHeader.rowSpan = 2;
        headerRow1.appendChild(activityHeader);
        MONTHS.forEach(m => {
            const th = document.createElement('th');
            th.textContent = m;
            th.style.fontWeight = 500; // Add font weight to month headers
            headerRow1.appendChild(th);
        });
        thead.appendChild(headerRow1);
        table.appendChild(thead);

        // Body (sow, plant, harvest)
        const tbody = document.createElement('tbody');
        ['sow', 'plant', 'harvest'].forEach((activity, idx) => {
            const row = document.createElement('tr');
            if (idx === 0) {
                // Only add the plant name cell for the first activity row, with rowspan=3
                const plantCell = document.createElement('td');
                plantCell.className = 'plant-name-cell';
                plantCell.style.width = '90px'; // Fixed width for consistency
                
                const imageFileName = getImageFileName(baseKey);
                
                // Try vegetable pictures first, then flower pictures
                const img = document.createElement('img');
                img.src = `src/assets/Vegetable Pictures/${imageFileName}`;
                img.alt = baseKey;
                img.loading = 'lazy';
                img.style.maxWidth = '40px';  // Reduced from 60px
                img.style.maxHeight = '40px'; // Reduced from 60px
                img.style.display = 'block';
                img.style.margin = '0 auto 2px auto'; // Reduced bottom margin
                
                // Add error handler to try flower pictures if vegetable picture fails
                img.onerror = function() {
                    this.src = `src/assets/Flower Pictures/${imageFileName.toLowerCase()}`;
                    this.onerror = function() {
                        // If both fail, hide the image and just show the text
                        this.style.display = 'none';
                    };
                };
                
                plantCell.appendChild(img);
                // Add plant name below image
                const nameDiv = document.createElement('div');
                nameDiv.textContent = baseKey;
                nameDiv.style.textAlign = 'center';
                nameDiv.style.fontSize = '0.8em'; // Smaller font size
                plantCell.appendChild(nameDiv);
                plantCell.rowSpan = 3;
                row.appendChild(plantCell);
            }
            const labelCell = document.createElement('td');
            labelCell.className = 'activity-label';
            labelCell.textContent = activity.toUpperCase();
            labelCell.style.fontSize = '0.8em'; // Smaller font size for activity labels
            row.appendChild(labelCell);
            for (let m = 1; m <= 12; m++) {
                const cell = document.createElement('td');
                cell.style.height = '8px'; // Fixed smaller height for cells
                if (data[activity] && data[activity].includes(m)) {
                    cell.style.background = ACTIVITY_COLORS[activity];
                    cell.style.padding = '0';
                    cell.style.lineHeight = '10px';
                    cell.style.verticalAlign = 'middle';
                }
                row.appendChild(cell);
            }
            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        block.appendChild(table);
        calendarContainer.appendChild(block);
    });

    // Add download button after all tables are created
    const downloadButton = document.createElement('button');
    downloadButton.className = 'download-btn';
    downloadButton.innerHTML = '<i class="fa fa-download"></i> Download Calendar';
    downloadButton.style.margin = '20px auto';
    downloadButton.style.display = 'block';
    
    downloadButton.addEventListener('click', async (e) => {
        try {
            // Show loading state
            downloadButton.disabled = true;
            downloadButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Generating...';
            
            // Get the entire garden calendar section
            const gardenCalendarSection = document.getElementById('garden-calendar');
            
            // Create a temporary container for capturing
            const captureContainer = document.createElement('div');
            captureContainer.style.padding = '40px';
            captureContainer.style.backgroundColor = '#ffffff';
            
            // Clone the section content
            const sectionClone = gardenCalendarSection.cloneNode(true);
            
            // Remove the download button from the clone
            const downloadBtnClone = sectionClone.querySelector('.download-btn');
            if (downloadBtnClone) {
                downloadBtnClone.remove();
            }
            
            // Add the clone to the capture container
            captureContainer.appendChild(sectionClone);
            
            // Temporarily add to document for capture
            document.body.appendChild(captureContainer);
            
            await loadExportLibraries();
            if (typeof window.html2canvas === 'undefined') throw new Error('html2canvas not loaded');
            const canvas = await window.html2canvas(captureContainer, {
                backgroundColor: '#ffffff',
                scale: 2, // Higher resolution
                logging: false,
                allowTaint: true,
                useCORS: true
            });
            
            // Remove the temporary container
            document.body.removeChild(captureContainer);
            
            // Create download link and open in new tab
            const imageDataUrl = canvas.toDataURL('image/png');
            const filename = 'Garden_Calendar.png';
            
            // Open in new tab
            window.open(imageDataUrl, '_blank');
            
            // Also trigger download
            const downloadLink = document.createElement('a');
            downloadLink.download = filename;
            downloadLink.href = imageDataUrl;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            trackDownload('calendar', filename);
            
            // Reset button state
            downloadButton.disabled = false;
            downloadButton.innerHTML = '<i class="fa fa-download"></i> Download Calendar';
        } catch (error) {
            console.error('Error generating calendar image:', error);
            alert('Error generating calendar image. Please try again.');
            downloadButton.disabled = false;
            downloadButton.innerHTML = '<i class="fa fa-download"></i> Download Calendar';
        }
    });
    
    calendarContainer.appendChild(downloadButton);
} 